import { EditorState, Modifier, RichUtils } from 'draft-js';
import { OrderedSet } from 'immutable';
import memoize from 'memoize-one';
import { createElement, PureComponent } from 'react';

import AlignmentControls from './AlignmentControls';
import CalloutControls from './CalloutControls';
import DropDownButton from './DropDownButton';
import LineSpacingControls from './LineSpacingControls';
import SolutionsControls from './SolutionsControls';
import TableControls from './TableControls';
import ToolbarButton from './ToolbarButton';
import ToolbarDivider from './ToolbarDivider';

import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { faListOl, faListUl, faPageBreak } from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default class BlockStyleControls extends PureComponent {
  toggleBlockType = blockType => {
    const { editorState, onChange } = this.props;
    onChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  setHeaderType = (blockType, config) => {
    const { editorState, onChange } = this.props;
    let newEditorState = RichUtils.toggleBlockType(editorState, blockType);
    if (config) {
      if (config.blockProps) {
        newEditorState = EditorState.push(
          newEditorState,
          Modifier.mergeBlockData(newEditorState.getCurrentContent(), newEditorState.getSelection(), config.blockProps),
          'change-block-data',
        );
      }
      if (config.styles) {
        const selection = newEditorState.getSelection();
        const block = newEditorState.getCurrentContent().getBlockForKey(selection.getAnchorKey());
        const lineSelection = selection.merge({ anchorOffset: 0, focusOffset: block.getLength(), isBackward: false });
        // newEditorState = EditorState.set(newEditorState, { selection: lineSelection });
        let newContent = newEditorState.getCurrentContent();
        config.styles.forEach(style => {
          newContent = Modifier.applyInlineStyle(newContent, lineSelection, style);
          // newEditorState = RichUtils.toggleInlineStyle(newEditorState, style);
        });
        newEditorState = EditorState.set(newEditorState, {
          currentContent: newContent,
          inlineStyleOverride: OrderedSet(config.styles),
        });
      }
    }
    onChange(newEditorState);
  };

  getHeaderOptions = memoize(config => {
    if (config === true) return HEADER_TYPES;
    return [
      HEADER_TYPES[0],
      ...config.map(headerCfg => {
        const [blockType, label, config] = Array.isArray(headerCfg) ? headerCfg : [headerCfg];
        const header = HEADER_TYPES.find(h => h.blockType === blockType);
        return {
          ...header,
          label: label || header.label,
          config,
        };
      }),
    ];
  });

  render() {
    const { block, nested, onAddAtomicBlock, onUpdateBlockData, plugins } = this.props;
    const currentBlockType = block?.getType() || '';
    const blockData = block?.getData();

    return (
      <>
        {plugins?.headerTypes && (
          <>
            <DropDownButton
              icon={
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    '& > :first-child': { width: 90 },
                  }}
                >
                  <Typography align="left" variant="body2">
                    {currentBlockType.match(/header/) ?
                      this.getHeaderOptions(plugins.headerTypes).find(t => t.blockType === currentBlockType)?.label
                    : HEADER_TYPES[0].label}{' '}
                  </Typography>
                  <FontAwesomeIcon icon={faCaretDown} size="xs" />
                </div>
              }
              menu
              tooltip="headings"
              // ToolbarButtonProps={{ active: blockType.match(/header/) }}
            >
              {this.getHeaderOptions(plugins.headerTypes).map(({ blockType, config, label, typographyVariant }, i) => (
                <MenuItem
                  key={i}
                  onMouseDown={() => this.setHeaderType(blockType, config)}
                  selected={blockType === currentBlockType}
                  sx={{ '&.Mui-selected': { backgroundColor: 'grey.200' } }}
                >
                  {createElement(typographyVariant, { style: { margin: 0, ...config?.htmlStyle } }, label)}
                </MenuItem>
              ))}
            </DropDownButton>
            <ToolbarDivider />
          </>
        )}
        {plugins?.listTypes && (
          <>
            <ToolbarButton
              active={currentBlockType === 'unordered-list-item'}
              onToggle={this.toggleBlockType}
              toggleKey="unordered-list-item"
              tooltip="bullet list"
            >
              <FontAwesomeIcon icon={faListUl} />
            </ToolbarButton>
            <ToolbarButton
              active={currentBlockType === 'ordered-list-item'}
              onToggle={this.toggleBlockType}
              toggleKey="ordered-list-item"
              tooltip="numbered list"
            >
              <FontAwesomeIcon icon={faListOl} />
            </ToolbarButton>
            {currentBlockType === 'ordered-list-item' && (
              <DropDownButton
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                icon={<FontAwesomeIcon icon={faCaretDown} size="xs" />}
                menu
                tooltip="change numbering"
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
              >
                {[...Array(5)].map((_, idx) => (
                  <MenuItem key={idx} onMouseDown={() => onUpdateBlockData({ olRestart: idx + 2 })}>
                    <span>
                      Restart numbering at <strong>{idx + 2}</strong>
                    </span>
                  </MenuItem>
                ))}
              </DropDownButton>
            )}
            <ToolbarDivider />
          </>
        )}
        {plugins?.alignment && (
          <>
            <AlignmentControls blockData={blockData} onUpdateBlockData={onUpdateBlockData} />
            <ToolbarDivider />
          </>
        )}
        {plugins?.lineSpacing && (
          <>
            <LineSpacingControls blockData={blockData} onUpdateBlockData={onUpdateBlockData} />
            <ToolbarDivider />
          </>
        )}
        {plugins?.callouts && <CalloutControls nested={nested} onAddAtomicBlock={onAddAtomicBlock} />}
        {plugins?.table && <TableControls nested={nested} onAddAtomicBlock={onAddAtomicBlock} />}
        {plugins?.solutions && <SolutionsControls blockData={blockData} onUpdateBlockData={onUpdateBlockData} />}
        {plugins?.pageBreak && (
          <>
            <ToolbarButton onToggle={() => onAddAtomicBlock('PAGE_BREAK')} tooltip="page break">
              <FontAwesomeIcon fixedWidth icon={faPageBreak} />
            </ToolbarButton>
            <ToolbarDivider />
          </>
        )}
      </>
    );
  }
}

const HEADER_TYPES = [
  { label: 'Normal text', blockType: 'unstyled', icon: 'p', typographyVariant: 'p' },
  { label: 'Header 1', blockType: 'header-one', icon: 'H1', typographyVariant: 'h1' },
  { label: 'Header 2', blockType: 'header-two', icon: 'H2', typographyVariant: 'h2' },
  { label: 'Header 3', blockType: 'header-three', icon: 'H3', typographyVariant: 'h3' },
  { label: 'Header 4', blockType: 'header-four', icon: 'H4', typographyVariant: 'h4' },
  { label: 'Header 5', blockType: 'header-five', icon: 'H5', typographyVariant: 'h5' },
  { label: 'Header 6', blockType: 'header-six', icon: 'H6', typographyVariant: 'h6' },
];
